import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Switch,
  FormControlLabel,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Code,
  Link as LinkIcon,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
  Add,
  Send,
  Delete,
  AttachFile,
  FormatQuote,
  Title,
  History,
  Schedule,
  Preview,
  Analytics,
  CheckCircle,
  Warning,
  Info,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  Settings,
  Palette,
  AccessTime,
  TrendingUp,
  Download,
} from '@mui/icons-material';

const AdvancedEmailComposer = ({
  compose = {},
  setCompose,
  accounts = [],
  carrierEmails = [],
  onSend,
  sendLoading = false,
  recipientStats = {},
  subjectInsights = {},
  bodyInsights = {},
  fileInputRef,
  onFileImport,
  uploadError,
  mergeTags = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [editorMode, setEditorMode] = useState('visual');
  const [previewMode, setPreviewMode] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [sendTrackingEnabled, setSendTrackingEnabled] = useState(true);
  const [sendReadReceipt, setSendReadReceipt] = useState(true);
  const [priority, setPriority] = useState('normal');
  const [draftHistory, setDraftHistory] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    formatting: true,
    mergeTags: true,
    recipients: true,
  });

  const templateColumns = [
    'Legal Name',
    'DBA',
    'DOT',
    'MC',
    'State(Physical)',
    'State(Mailing)',
    'Phone',
    'Email',
    'Status',
    'Carrier Operation',
    'Power Units',
    'Drivers',
    'Active Duration',
    'Physical Address',
    'Mailing Address',
  ];

  const downloadTemplate = () => {
    const header = templateColumns.join(',');
    const rows = [
      ['Alpha Logistics LLC', 'Alpha Logistics', '1234567', 'MC123456', 'TX', 'TX', '0300-1234567', 'dispatch@alphalogistics.com', 'Active', 'Carrier', '25', '30', '3 years', '123 Main St, Dallas, TX', 'PO Box 45, Dallas, TX'],
      ['Blue Ridge Freight', 'Blue Ridge', '7654321', 'MC765432', 'CA', 'CA', '0312-5550188', 'ops@blueridgefreight.com', 'Active', 'Carrier', '12', '14', '1 year', '456 Market St, San Francisco, CA', '456 Market St, San Francisco, CA'],
      ['Sunset Transport Inc', 'Sunset', '2468135', 'MC246813', 'IL', 'IL', '0321-5550147', 'team@sunsettransport.com', 'Inactive', 'Broker', '8', '10', '6 months', '789 Lake Shore Dr, Chicago, IL', '789 Lake Shore Dr, Chicago, IL'],
    ];
    const csvLines = [header, ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([`${csvLines}\n`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'email_recipients_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const textareaRef = useRef(null);

  // Auto-save drafts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (compose.body || compose.subject) {
        const newDraft = {
          id: Date.now(),
          subject: compose.subject,
          body: compose.body.substring(0, 100),
          timestamp: new Date().toLocaleTimeString(),
        };
        setDraftHistory((prev) => [newDraft, ...prev].slice(0, 10));
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [compose.body, compose.subject]);

  const insertMarkdown = (before, after = '') => {
    const textarea = textareaRef.current || document.querySelector('[name="compose-body"]');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = compose.body || '';
    const selectedText = text.substring(start, end) || 'text';
    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);

    setCompose((prev) => ({ ...prev, body: newText }));
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
    }, 0);
  };

  const applyFormat = (format) => {
    switch (format) {
      case 'bold':
        insertMarkdown('**', '**');
        break;
      case 'italic':
        insertMarkdown('*', '*');
        break;
      case 'underline':
        insertMarkdown('<u>', '</u>');
        break;
      case 'code':
        insertMarkdown('```', '```');
        break;
      case 'quote':
        insertMarkdown('> ', '');
        break;
      case 'link':
        insertMarkdown('[Link](', ')');
        break;
      case 'heading':
        insertMarkdown('## ', '');
        break;
      case 'bullet':
        insertMarkdown('• ', '');
        break;
      case 'number':
        insertMarkdown('1. ', '');
        break;
      default:
        break;
    }
  };

  const insertMergeTag = (tag) => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const text = compose.body || '';
      const newText = text.substring(0, start) + tag + text.substring(start);
      setCompose((prev) => ({ ...prev, body: newText }));
      setTimeout(() => {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(start + tag.length, start + tag.length);
      }, 0);
    }
  };

  const handleRecipientsFromManual = () => {
    const emails = compose.manualBlock
      .split(/[,\n\s]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

    setCompose((prev) => ({
      ...prev,
      recipients: [...new Set([...(prev.recipients || []), ...emails])],
      manualBlock: '',
    }));
  };

  const copyDraft = (draft) => {
    setCompose((prev) => ({
      ...prev,
      subject: draft.subject,
      body: draft.body,
    }));
    setHistoryOpen(false);
  };

  const calculateSendTime = () => {
    if (scheduleDate && scheduleTime) {
      return `Scheduled for ${scheduleDate} at ${scheduleTime}`;
    }
    return 'Send immediately';
  };

  const getDeliverabilityScore = () => {
    let score = 50;
    if (compose.subject && compose.subject.length > 20 && compose.subject.length < 65) score += 15;
    if (compose.body && compose.body.length > 100) score += 15;
    if (bodyInsights?.hasGreeting) score += 10;
    if (!subjectInsights?.spamHits?.length) score += 10;
    return Math.min(score, 100);
  };

  const score = getDeliverabilityScore();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {/* Account & Subject Section */}
      <Grid container spacing={isMobile ? 1 : 2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Send from account"
            value={compose.accountId || ''}
            onChange={(e) => setCompose((prev) => ({ ...prev, accountId: e.target.value }))}
            size={isMobile ? 'small' : 'medium'}
            variant="outlined"
          >
            <MenuItem value="">Auto-select</MenuItem>
            {accounts.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.label || acc.fromEmail} • {acc.fromName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email Subject"
            value={compose.subject}
            onChange={(e) => setCompose((prev) => ({ ...prev, subject: e.target.value }))}
            size={isMobile ? 'small' : 'medium'}
            placeholder="Enter compelling subject line"
            helperText={`${compose.subject.length}/70 chars`}
            error={subjectInsights?.tooShort || subjectInsights?.tooLong}
          />
        </Grid>
      </Grid>

      {/* Subject Insights */}
      {compose.subject && (
        <Card variant="outlined" sx={{ background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)' }}>
          <CardContent sx={{ py: 1, px: isMobile ? 1.5 : 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
              {subjectInsights?.tooShort && (
                <Chip size="small" icon={<Warning />} label="Subject too short" color="warning" />
              )}
              {subjectInsights?.tooLong && (
                <Chip size="small" icon={<Warning />} label="Subject too long" color="warning" />
              )}
              {subjectInsights?.spamHits?.length > 0 && (
                <Chip
                  size="small"
                  icon={<Warning />}
                  label={`Spam triggers: ${subjectInsights.spamHits.slice(0, 2).join(', ')}`}
                  color="error"
                />
              )}
              {!subjectInsights?.tooShort && !subjectInsights?.tooLong && !subjectInsights?.spamHits?.length && (
                <Chip size="small" icon={<CheckCircle />} label="✅ Subject looks great!" color="success" />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Message Body with Formatting */}
      <Card variant="outlined" sx={{ overflow: 'hidden' }}>
        {/* Collapsible Sections */}
        <Box sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          {/* Formatting Toolbar Section */}
          <Box
            sx={{
              p: isMobile ? 1 : 1.5,
              backgroundColor: '#fafafa',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={expandedSections.formatting ? 1 : 0}
            >
              <Typography variant="caption" fontWeight={700}>
                📝 Formatting Tools
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setExpandedSections((prev) => ({
                    ...prev,
                    formatting: !prev.formatting,
                  }))
                }
              >
                {expandedSections.formatting ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>

            <Collapse in={expandedSections.formatting}>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}
              >
                {[
                  { icon: <FormatBold />, label: 'Bold', action: 'bold' },
                  { icon: <FormatItalic />, label: 'Italic', action: 'italic' },
                  { icon: <FormatUnderlined />, label: 'Underline', action: 'underline' },
                  { icon: <Code />, label: 'Code', action: 'code' },
                  { icon: <FormatQuote />, label: 'Quote', action: 'quote' },
                  { icon: <LinkIcon />, label: 'Link', action: 'link' },
                  { icon: <Title />, label: 'Heading', action: 'heading' },
                  { icon: <FormatListBulleted />, label: 'Bullet', action: 'bullet' },
                  { icon: <FormatListNumbered />, label: 'Number', action: 'number' },
                  { icon: <FormatClear />, label: 'Clear', action: 'clear' },
                ].map((btn) => (
                  <Tooltip key={btn.action} title={btn.label}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (btn.action === 'clear') {
                          setCompose((prev) => ({ ...prev, body: '' }));
                        } else {
                          applyFormat(btn.action);
                        }
                      }}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                          color: '#667eea',
                        },
                      }}
                    >
                      {btn.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </Stack>
            </Collapse>
          </Box>

          {/* Merge Tags Section */}
          <Box sx={{ p: isMobile ? 1 : 1.5, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={expandedSections.mergeTags ? 1 : 0}
            >
              <Typography variant="caption" fontWeight={700}>
                🏷️ Merge Tags (Personalization)
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setExpandedSections((prev) => ({
                    ...prev,
                    mergeTags: !prev.mergeTags,
                  }))
                }
              >
                {expandedSections.mergeTags ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>

            <Collapse in={expandedSections.mergeTags}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ p: 1 }}>
                {(mergeTags.length
                  ? mergeTags
                  : [
                      '{{first_name}}',
                      '{{last_name}}',
                      '{{company}}',
                      '{{city}}',
                      '{{phone}}',
                      '{{email}}',
                    ]
                ).map((tag) => (
                  <Chip
                    key={tag}
                    size="small"
                    label={tag}
                    onClick={() => insertMergeTag(tag)}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#e3f2fd' },
                    }}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>
        </Box>

        {/* Message Body */}
        <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
          {!previewMode ? (
            <TextField
              fullWidth
              multiline
              minRows={isMobile ? 6 : 10}
              maxRows={20}
              name="compose-body"
              value={compose.body}
              onChange={(e) => setCompose((prev) => ({ ...prev, body: e.target.value }))}
              placeholder="Compose your professional email here... Use formatting and merge tags for better engagement"
              variant="outlined"
              inputRef={textareaRef}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: isMobile ? '0.875rem' : '1rem',
                },
              }}
            />
          ) : (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#f9f9f9',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                minHeight: isMobile ? 200 : 300,
                fontSize: '0.95rem',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
              }}
            >
              {compose.body || '(No message body)'}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Message Insights */}
      {compose.body && (
        <Card variant="outlined" sx={{ background: 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, rgba(56,142,60,0.1) 100%)' }}>
          <CardContent sx={{ py: 1, px: isMobile ? 1.5 : 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
              <Stack>
                <Typography fontWeight={700} variant="body2">
                  📊 Message Insights
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {bodyInsights?.wordCount || 0} words • {bodyInsights?.linkCount || 0} links • {compose.body.length} chars
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Chip
                  size="small"
                  icon={bodyInsights?.wordCount > 0 ? <CheckCircle /> : <Info />}
                  label={`${bodyInsights?.wordCount || 0} words`}
                  color={bodyInsights?.wordCount > 0 ? 'success' : 'default'}
                />
                <Chip size="small" label={`${bodyInsights?.linkCount || 0} links`} variant="outlined" />
                {bodyInsights?.hasGreeting && (
                  <Chip size="small" icon={<CheckCircle />} label="✅ Greeting" color="success" />
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Recipients Section - Collapsible */}
      <Card variant="outlined" sx={{ border: '2px dashed #667eea' }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={expandedSections.recipients ? 1 : 0}
          >
            <Typography fontWeight={700}>👥 Recipients ({recipientStats?.total || 0})</Typography>
            <IconButton
              size="small"
              onClick={() =>
                setExpandedSections((prev) => ({
                  ...prev,
                  recipients: !prev.recipients,
                }))
              }
            >
              {expandedSections.recipients ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>

          <Collapse in={expandedSections.recipients}>
            <Stack spacing={2}>
              {/* Action Buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleRecipientsFromManual}
                  disabled={!compose.manualBlock.trim()}
                  fullWidth={isMobile}
                >
                  Add from list
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={downloadTemplate}
                  fullWidth={isMobile}
                >
                  Download template
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFile />}
                  onClick={() => fileInputRef?.current?.click()}
                  fullWidth={isMobile}
                >
                  Upload CSV/Excel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  disabled={!carrierEmails.length}
                  onClick={() =>
                    setCompose((prev) => ({
                      ...prev,
                      recipients: [
                        ...(prev.recipients || []),
                        ...carrierEmails.filter((e) => !prev.recipients?.includes(e)),
                      ],
                    }))
                  }
                  fullWidth={isMobile}
                >
                  + {carrierEmails.length} Carriers
                </Button>
              </Stack>

              {/* Manual Input */}
              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Paste emails separated by comma or new line"
                value={compose.manualBlock}
                onChange={(e) => setCompose((prev) => ({ ...prev, manualBlock: e.target.value }))}
                size="small"
              />

              {uploadError && <Alert severity="warning">{uploadError}</Alert>}

              {/* Recipient Display */}
              {compose.recipients?.length > 0 && (
                <Box>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {compose.recipients.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() =>
                          setCompose((prev) => ({
                            ...prev,
                            recipients: prev.recipients.filter((r) => r !== email),
                          }))
                        }
                        size="small"
                        color="primary"
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Collapse>
        </CardContent>
      </Card>

      {/* Send Section */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<Delete />} onClick={() => setCompose({ accountId: '', subject: '', body: '', recipients: [], manualBlock: '' })}>
          Clear
        </Button>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={onSend}
          disabled={sendLoading || !compose.subject.trim() || !compose.body.trim() || !compose.recipients?.length}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontWeight: 700,
            minWidth: isMobile ? 'auto' : 150,
          }}
        >
          {sendLoading ? 'Sending...' : '✈️ Send'}
        </Button>
      </Box>

      {/* Draft History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>📜 Draft History</DialogTitle>
        <DialogContent>
          {draftHistory.length === 0 ? (
            <Typography color="text.secondary">No drafts saved yet</Typography>
          ) : (
            <List>
              {draftHistory.map((draft) => (
                <ListItem
                  key={draft.id}
                  button
                  onClick={() => copyDraft(draft)}
                  sx={{ borderBottom: '1px solid #eee' }}
                >
                  <ListItemText
                    primary={draft.subject || '(No subject)'}
                    secondary={`${draft.body}... • ${draft.timestamp}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>🕐 Schedule Email</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} fullWidth />
            <TextField type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} fullWidth />
            <Alert severity="info">{calculateSendTime()}</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
          <Button variant="contained">Schedule</Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>⚙️ Email Settings</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <FormControlLabel
              control={<Switch checked={sendTrackingEnabled} onChange={(e) => setSendTrackingEnabled(e.target.checked)} />}
              label="Enable open tracking"
            />
            <FormControlLabel
              control={<Switch checked={sendReadReceipt} onChange={(e) => setSendReadReceipt(e.target.checked)} />}
              label="Request read receipt"
            />
            <TextField select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)} fullWidth>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>📈 Email Analytics Preview</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">Deliverability Score</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress variant="determinate" value={score} />
                </Box>
                <Typography fontWeight={700}>{score}%</Typography>
              </Stack>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2">📊 Expected Performance</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Open Rate" secondary="~25-35% (industry avg)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Click Rate" secondary="~2-5% (industry avg)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Reply Rate" secondary="~0.5-1% (industry avg)" />
                </ListItem>
              </List>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={onFileImport}
      />
    </Box>
  );
};

export default AdvancedEmailComposer;

